export type LogLevel = 'info' | 'warn' | 'error';

export interface LogContext {
  /** Feature or module name (e.g. 'auth', 'address-store', 'api-client') */
  feature?: string;
  /** Route, operation, or action name (e.g. 'fetchUser', 'set-token') */
  route?: string;
  /** Additional structured metadata (avoid PII whenever possible) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface Logger {
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: unknown, context?: LogContext): void;
}

function formatContext(context?: LogContext): unknown {
  if (!context) return '';
  return context;
}

function createLogger(): Logger {
  const isProd = process.env.NODE_ENV === 'production' || true;

  return {
    info(message: string, context?: LogContext) {
      // In production we generally don't want noisy info logs to stdout.
      if (isProd) return;
      // eslint-disable-next-line no-console
      console.info(message, formatContext(context));
    },
    warn(message: string, context?: LogContext) {
      console.warn(message, formatContext(context));
    },
    error(message: string, error?: unknown, context?: LogContext) {
      if (error !== undefined) {
        console.error(message, error, formatContext(context));
      } else {
        console.error(message, formatContext(context));
      }
      forwardErrorToRollbar(message, error, context);
    },
  };
}

/**
 * Forward error logs to Rollbar when running in production with a configured
 * access token. Imported lazily to avoid pulling the SDK into tests / SSR.
 */
function forwardErrorToRollbar(message: string, error?: unknown, context?: LogContext): void {
  if (typeof window === 'undefined') return;
  // Dynamic import keeps tests + SSR free of the Rollbar dependency. The
  // helper is a lazy singleton: returns null when disabled (dev or missing
  // token), so the cost in those modes is one nullish-check per call.
  void import('./rollbar')
    .then(({ getRollbar }) => {
      const rb = getRollbar();
      if (!rb) return;
      if (error instanceof Error) {
        rb.error(error, { message, ...context });
      } else if (error !== undefined) {
        rb.error(message, { error, ...context });
      } else {
        rb.error(message, context);
      }
    })
    .catch(() => {
      /* swallow: Rollbar reporting must never break the call site */
    });
}

/**
 * Singleton logger instance.
 *
 * Use this instead of direct console.* calls so that logging remains
 * consistent across the application and can be redirected to external
 * providers in the future without changing call sites.
 */
export const logger: Logger = createLogger();
